/**
 * QuickDelivery Pro — data model and pure business logic.
 * Shared by the web app, the Expo app and the licence server.
 */

export interface Product {
  id: string;
  name: string;
  price: number;
  image?: string;
  category?: string;
  /** Soft archive: keeps history intact when a product is discontinued. */
  archived?: boolean;
}

export interface Client {
  id: string;
  name: string;
  /** Canonical English weekday names, see DAY_KEYS. */
  deliveryDays: string[];
  city: string;
  sector: string;
  address?: string;
  phone?: string;
  note?: string;
  archived?: boolean;
}

export interface OrderItem {
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
}

export interface Order {
  id: string;
  clientId: string;
  clientName: string;
  items: OrderItem[];
  totalAmount: number;
  /** Amount actually collected; `totalAmount` when fully paid. */
  paidAmount: number;
  timestamp: number;
  /** Local-time YYYY-MM-DD. See localDateString. */
  dateString: string;
  note?: string;
}

export const DAY_KEYS = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
] as const;
export type DayKey = (typeof DAY_KEYS)[number];

export type Language = 'fr' | 'ar' | 'en';

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

/**
 * YYYY-MM-DD in the *device's* timezone.
 *
 * v1 used `new Date().toISOString().split('T')[0]`, which is UTC. Morocco runs
 * at UTC+1, so every order placed after 23:00 local was filed under tomorrow —
 * it vanished from "today's sales" and landed on the wrong day in analytics.
 */
export function localDateString(d: Date | number = Date.now()): string {
  const date = typeof d === 'number' ? new Date(d) : d;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Today's weekday as a DAY_KEYS entry (JS weeks start Sunday; ours start Monday). */
export function todayKey(d: Date = new Date()): DayKey {
  const js = d.getDay();
  return DAY_KEYS[js === 0 ? 6 : js - 1];
}

export function startOfWeek(d: Date = new Date()): Date {
  const out = new Date(d);
  const js = out.getDay();
  out.setDate(out.getDate() - (js === 0 ? 6 : js - 1));
  out.setHours(0, 0, 0, 0);
  return out;
}

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

/**
 * Round to 2dp through integer minor units.
 *
 * Line subtotals in v1 were raw float products, so a 12-line order could drift
 * a centime away from the sum the driver counted in cash.
 */
export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

export function lineSubtotal(quantity: number, unitPrice: number): number {
  return round2(quantity * unitPrice);
}

export function orderTotal(items: OrderItem[]): number {
  return round2(items.reduce((sum, i) => sum + i.subtotal, 0));
}

// ---------------------------------------------------------------------------
// Aggregations (pure, so analytics can be unit-tested)
// ---------------------------------------------------------------------------

export interface Stats {
  totalSales: number;
  totalCollected: number;
  outstanding: number;
  orderCount: number;
  activeClients: number;
  avgOrder: number;
}

export function computeStats(orders: Order[]): Stats {
  const totalSales = round2(orders.reduce((s, o) => s + o.totalAmount, 0));
  const totalCollected = round2(orders.reduce((s, o) => s + (o.paidAmount ?? o.totalAmount), 0));
  return {
    totalSales,
    totalCollected,
    outstanding: round2(totalSales - totalCollected),
    orderCount: orders.length,
    activeClients: new Set(orders.map((o) => o.clientId)).size,
    avgOrder: orders.length ? round2(totalSales / orders.length) : 0,
  };
}

export function salesByDay(orders: Order[], days = 7): { name: string; value: number }[] {
  const map = new Map<string, number>();
  for (const o of orders) map.set(o.dateString, round2((map.get(o.dateString) ?? 0) + o.totalAmount));
  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-days)
    .map(([date, value]) => ({ name: date.slice(5).replace('-', '/'), value }));
}

export function salesBySector(orders: Order[], clients: Client[], unknownLabel: string) {
  const sectorOf = new Map(clients.map((c) => [c.id, c.sector || unknownLabel]));
  const map = new Map<string, number>();
  for (const o of orders) {
    const sector = sectorOf.get(o.clientId) ?? unknownLabel;
    map.set(sector, round2((map.get(sector) ?? 0) + o.totalAmount));
  }
  return [...map.entries()].sort((a, b) => b[1] - a[1]).map(([name, value]) => ({ name, value }));
}

export function topProducts(orders: Order[], limit = 5) {
  const map = new Map<string, { units: number; revenue: number }>();
  for (const o of orders) {
    for (const item of o.items) {
      const prev = map.get(item.productName) ?? { units: 0, revenue: 0 };
      map.set(item.productName, {
        units: prev.units + item.quantity,
        revenue: round2(prev.revenue + item.subtotal),
      });
    }
  }
  return [...map.entries()]
    .sort((a, b) => b[1].revenue - a[1].revenue)
    .slice(0, limit)
    .map(([name, v]) => ({ name, value: v.units, revenue: v.revenue }));
}

export function topClients(orders: Order[], limit = 5) {
  const map = new Map<string, { name: string; revenue: number; orders: number }>();
  for (const o of orders) {
    const prev = map.get(o.clientId) ?? { name: o.clientName, revenue: 0, orders: 0 };
    map.set(o.clientId, {
      name: o.clientName,
      revenue: round2(prev.revenue + o.totalAmount),
      orders: prev.orders + 1,
    });
  }
  return [...map.values()].sort((a, b) => b.revenue - a.revenue).slice(0, limit);
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

function csvCell(v: unknown): string {
  const s = String(v ?? '');
  // Guard against CSV injection when the file is opened in Excel.
  const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return /[",\n;]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/** One row per order line, which is what accountants actually want. */
export function ordersToCsv(orders: Order[], currency: string): string {
  const header = [
    'date', 'time', 'order_id', 'client', 'product', 'quantity',
    `unit_price_${currency}`, `subtotal_${currency}`, `order_total_${currency}`, `paid_${currency}`,
  ];
  const rows = orders.flatMap((o) => {
    const t = new Date(o.timestamp);
    const time = `${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`;
    return o.items.map((i) => [
      o.dateString, time, o.id, o.clientName, i.productName, i.quantity,
      i.unitPrice.toFixed(2), i.subtotal.toFixed(2), o.totalAmount.toFixed(2),
      (o.paidAmount ?? o.totalAmount).toFixed(2),
    ]);
  });
  // BOM so Excel opens Arabic client names correctly.
  return '﻿' + [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n');
}

export interface Backup {
  version: 2;
  exportedAt: number;
  products: Product[];
  clients: Client[];
  orders: Order[];
}

export function makeBackup(products: Product[], clients: Client[], orders: Order[]): Backup {
  return { version: 2, exportedAt: Date.now(), products, clients, orders };
}

/** Validates a restore payload rather than trusting it — it comes from a file. */
export function parseBackup(json: string): Backup {
  const data = JSON.parse(json);
  if (!data || typeof data !== 'object') throw new Error('invalid-backup');
  const arr = (v: unknown) => (Array.isArray(v) ? v : []);
  return {
    version: 2,
    exportedAt: typeof data.exportedAt === 'number' ? data.exportedAt : Date.now(),
    products: arr(data.products),
    clients: arr(data.clients),
    orders: arr(data.orders).map((o: Order) => ({
      ...o,
      paidAmount: typeof o.paidAmount === 'number' ? o.paidAmount : o.totalAmount,
      // v1 wrote UTC date strings; recompute from the timestamp on the way in.
      dateString: o.timestamp ? localDateString(o.timestamp) : o.dateString,
    })),
  };
}
